const STORAGE_KEY = 'sonny.onboarding.tenantSlug';

export const TENANT_QUERY_PARAM = 'tenant';

const TENANT_SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{0,63}$/;

const SUBDOMAIN_PATTERN = /^([a-z0-9][a-z0-9-]*)\.(entry|onboarding)\.identila\.com\.br$/;

// Rotas do funil ocupam a raiz — um path de um segmento só é slug se não for uma delas.
// Compartilhado com eventSlug.ts (slug de EVENTO também entra pelo path).
export const RESERVED_PATH_SEGMENTS = new Set([
  'welcome',
  'terms',
  'identify',
  'invite',
  'link',
  'mobile',
  'selfie',
  'signup',
  'companions',
  'success',
  'parental-consent',
  'error',
  'api',
]);

export type TenantSlugLocation = {
  hostname: string;
  pathname: string;
  search: string;
};

export function normalizeTenantSlug(value: string | null | undefined): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const slug = value.trim().toLowerCase();
  return TENANT_SLUG_PATTERN.test(slug) ? slug : null;
}

function slugFromQuery(search: string): string | null {
  return normalizeTenantSlug(new URLSearchParams(search).get(TENANT_QUERY_PARAM));
}

function slugFromSubdomain(hostname: string): string | null {
  const match = SUBDOMAIN_PATTERN.exec(hostname.toLowerCase());
  return match ? normalizeTenantSlug(match[1]) : null;
}

function slugFromPath(pathname: string): string | null {
  const segments = pathname.split('/').filter((segment) => segment !== '');
  if (segments.length !== 1) {
    return null;
  }
  const [segment] = segments;
  if (segment === undefined || RESERVED_PATH_SEGMENTS.has(segment.toLowerCase())) {
    return null;
  }
  return normalizeTenantSlug(segment);
}

export function resolveTenantSlugFromLocation(location: TenantSlugLocation): string | null {
  return (
    slugFromQuery(location.search) ??
    slugFromSubdomain(location.hostname) ??
    slugFromPath(location.pathname)
  );
}

function canUseSessionStorage(): boolean {
  try {
    return typeof sessionStorage !== 'undefined';
  } catch {
    return false;
  }
}

export function setStoredTenantSlug(slug: string): void {
  if (slug === '' || !canUseSessionStorage()) {
    return;
  }
  try {
    sessionStorage.setItem(STORAGE_KEY, slug);
  } catch {
    // Modo privado ou storage desabilitado — o slug segue valendo só para este load.
  }
}

export function getStoredTenantSlug(): string | null {
  if (!canUseSessionStorage()) {
    return null;
  }
  try {
    return normalizeTenantSlug(sessionStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}
